import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useCustomerWorkspace } from "../../context/CustomerWorkspaceContext";
import {
  fetchMediaByCustomer,
  deleteMedia,
} from "../../services/contentService";
import { MediaItem } from "../../types/MediaItem";
import ConfirmDialog from "../Shared/ConfirmDialog";
import { CognitoAuthService } from "../../services/cognitoService";
import { uploadMedia } from "../../features/media/utils/mediaUploadUtils";
import {
  parseTagInput,
  collectExistingTags,
  userTags,
} from "../../features/media/utils/mediaTags";
import { debug } from "../../utils/debugLogger";
import { useToast } from "../../context/ToastContext";

/** MIME types allowed for toolbar upload (images + videos only). */
const ALLOWED_IMAGE_MIMES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);
const ALLOWED_VIDEO_MIMES = new Set([
  "video/mp4",
  "video/quicktime",
  "video/webm",
]);
const ALLOWED_DOCUMENT_MIMES = new Set([
  "application/pdf",
]);

function isAllowedMediaFile(file: File): boolean {
  const t = file.type.toLowerCase();
  if (t.startsWith("image/")) return ALLOWED_IMAGE_MIMES.has(t);
  if (t.startsWith("video/")) return ALLOWED_VIDEO_MIMES.has(t);
  if (t === "application/pdf") return ALLOWED_DOCUMENT_MIMES.has(t);
  if (file.name.toLowerCase().endsWith(".pdf")) return true;
  return false;
}

function createThumbnailFromFile(file: File): Promise<string> {
  return new Promise((resolve) => {
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => resolve((e.target?.result as string) || "");
      reader.readAsDataURL(file);
      return;
    }
    if (file.type.startsWith("video/")) {
      const video = document.createElement("video");
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      video.addEventListener("loadeddata", () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const seekTime = Math.min(1, video.duration * 0.1);
        video.currentTime = seekTime;
      });
      video.addEventListener("seeked", () => {
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", 0.8));
        } else {
          resolve("");
        }
        URL.revokeObjectURL(video.src);
      });
      video.addEventListener("error", () => {
        resolve("");
        URL.revokeObjectURL(video.src);
      });
      video.src = URL.createObjectURL(file);
      video.load();
      return;
    }
    resolve("");
  });
}

interface DisplayMediaProps {
  isSelectionMode?: boolean;
  onMediaSelected?: (media: MediaItem) => void;
  onClose?: () => void;
  /** Show Upload media control in the toolbar (e.g. Media Library page). */
  showToolbarUpload?: boolean;
  /** After a video upload, optional hook for Save to Platform (thumbnail = data URL). */
  onPrepareForSocial?: (videoUrl: string, thumbnailUrl: string) => void;
}

const PAGE_SIZE = 12;
const BUTTON_SIZE = 44;
const BUTTON_GAP = 5;
const BUTTON_RIGHT_MARGIN = 5;
const IMAGE_LEFT_MARGIN = 14;
const IMAGE_RIGHT_PADDING = BUTTON_SIZE + BUTTON_GAP * 4 + BUTTON_RIGHT_MARGIN;

const DisplayMedia: React.FC<DisplayMediaProps> = ({
  isSelectionMode,
  onMediaSelected,
  onClose,
  showToolbarUpload = false,
  onPrepareForSocial,
}) => {
  const { idToken } = useAuth();
  const { showToast } = useToast();
  const { selectedCustomerId } = useCustomerWorkspace();
  const [mediaTab, setMediaTab] = useState<"images" | "videos" | "documents">("images");
  const [items, setItems] = useState<MediaItem[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [pageTokens, setPageTokens] = useState<(string | null)[]>([null]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<MediaItem | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteItem, setPendingDeleteItem] = useState<MediaItem | null>(
    null
  );
  const [uploadBusy, setUploadBusy] = useState(false);
  // Applied to every file in the next upload. Deliberately kept after
  // uploading: a customer adding a campaign across several batches should not
  // retype the tag, which is exactly how one campaign ends up under three.
  const [uploadTags, setUploadTags] = useState("");
  // Only the customer's own tags — see collectExistingTags. Empty until they
  // have tagged something, which is what keeps the field a plain text box.
  const tagSuggestions = useMemo(() => collectExistingTags(items), [items]);
  const [lastUploadedVideoForSocial, setLastUploadedVideoForSocial] =
    useState<{ videoUrl: string; thumbnailUrl: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  // Modal Esc close
  useEffect(() => {
    if (!preview) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setConfirmDeleteOpen(false);
        setPendingDeleteItem(null);
        setPreview(null);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [preview]);

  // If the preview closes for any reason, also close the delete confirmation dialog.
  useEffect(() => {
    if (!preview) {
      setConfirmDeleteOpen(false);
      setPendingDeleteItem(null);
    }
  }, [preview]);

  const handleSelectMedia = (media: MediaItem) => {
    if (isSelectionMode && onMediaSelected) {
      onMediaSelected(media);
    }
  };
  const mediaTypeParam = mediaTab === "images" ? "image" : mediaTab === "videos" ? "video" : "document";

  // Fetch page 1 — resets the list. Used on mount, tab change, customer change, upload, delete.
  const fetchPage1 = useCallback(async () => {
    if (!selectedCustomerId) return;
    if (!idToken) {
      showToast("Authentication token missing.", "error");
      return;
    }
    // Cancel any in-flight request.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const reqId = ++requestIdRef.current;

    setLoading(true);
    setItems([]);
    setNextToken(null);
    setPageTokens([null]);
    setCurrentPage(0);
    setPreview(null);

    try {
      const result = await fetchMediaByCustomer(selectedCustomerId, idToken, {
        limit: PAGE_SIZE,
        mediaType: mediaTypeParam,
        signal: controller.signal,
      });
      if (reqId !== requestIdRef.current) return; // stale
      setItems(result.medias);
      setNextToken(result.nextToken);
    } catch (err: any) {
      if (err.name === "AbortError") return;
      showToast(err.message || "Error loading media.", "error");
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [selectedCustomerId, idToken, mediaTypeParam]);

  const fetchPage = useCallback(async (startToken: string | null) => {
    if (!selectedCustomerId || !idToken) return;
    const reqId = requestIdRef.current;
    setLoading(true);
    try {
      const result = await fetchMediaByCustomer(selectedCustomerId, idToken, {
        limit: PAGE_SIZE,
        nextToken: startToken,
        mediaType: mediaTypeParam,
      });
      if (reqId !== requestIdRef.current) return;
      setItems(result.medias);
      setNextToken(result.nextToken);
    } catch (err: any) {
      showToast(err.message || "Error loading media.", "error");
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [selectedCustomerId, idToken, mediaTypeParam]);

  const handleNextPage = useCallback(async () => {
    if (!nextToken) return;
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    setPageTokens((prev) => {
      const updated = [...prev];
      updated[newPage] = nextToken;
      return updated;
    });
    await fetchPage(nextToken);
  }, [nextToken, currentPage, fetchPage]);

  const handlePrevPage = useCallback(async () => {
    if (currentPage === 0) return;
    const newPage = currentPage - 1;
    setCurrentPage(newPage);
    await fetchPage(pageTokens[newPage] ?? null);
  }, [currentPage, pageTokens, fetchPage]);

  // Re-fetch page 1 whenever customer or tab changes.
  useEffect(() => {
    fetchPage1();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchPage1]);

  const handleToolbarFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!selectedCustomerId || !idToken) {
      showToast("Choose a business profile first.", "warning");
      return;
    }
    const allowed = files.filter(isAllowedMediaFile);
    const skipped = files.length - allowed.length;
    if (allowed.length === 0) {
      if (skipped) {
        showToast("Unsupported file type selected. Use PNG, JPEG, GIF, WebP, MP4, MOV, WebM, or PDF.", "warning");
      } else {
        showToast("No files selected.", "info");
      }
      return;
    }
    setUploadBusy(true);
    setLastUploadedVideoForSocial(null);
    try {
      const userEmail = CognitoAuthService.getUserEmail(idToken);
      let lastVideo: { url: string; thumb: string } | null = null;
      for (const file of allowed) {
        const result = await uploadMedia({
          file,
          customerId: selectedCustomerId,
          userEmail,
          token: idToken,
          isLogo: false,
          mediaType: "raw",
          additionalMetadata: { uploaded_via: "media_library_toolbar" },
          tags: parseTagInput(uploadTags),
        });
        if (result.success && file.type.startsWith("video/")) {
          const thumb = await createThumbnailFromFile(file);
          lastVideo = {
            url: result.cloudFrontUrl,
            thumb,
          };
        }
      }
      if (lastVideo && onPrepareForSocial) {
        setLastUploadedVideoForSocial({
          videoUrl: lastVideo.url,
          thumbnailUrl: lastVideo.thumb,
        });
      }
      const successMsg = skipped > 0
        ? `Uploaded ${allowed.length} file(s). ${skipped} skipped (unsupported).`
        : `Uploaded ${allowed.length} file(s) successfully.`;

      showToast(successMsg, skipped > 0 ? "warning" : "success");
      await fetchPage1();
    } catch (err: any) {
      showToast(err?.message || "Upload failed.", "error");
    } finally {
      setUploadBusy(false);
    }
  };

  function formatDateNoSeconds(created_at: string) {
    const date = new Date(created_at);
    if (isNaN(date.getTime())) return created_at;
    return date.toLocaleString([], {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  // Delete media handler
  const handleDeleteImage = (item: MediaItem) => {
    // Client-side validation to prevent bad delete requests.
    if (loading) return;
    const customerId = selectedCustomerId;
    const mediaId = item?.media_id;

    if (!customerId) {
      showToast("Select a business profile before deleting media.", "warning");
      return;
    }

    if (!idToken) {
      showToast("Authentication token missing. Please log in again.", "error");
      return;
    }

    if (!mediaId) {
      showToast("Invalid media selection.", "error");
      return;
    }

    // Validation passed: open confirmation dialog (instead of using window.confirm).
    setPendingDeleteItem(item);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    const item = pendingDeleteItem;
    if (!item) return;
    if (loading) return;

    const customerId = selectedCustomerId;
    const mediaId = item?.media_id;

    if (!customerId) {
      showToast("Select a business profile first.", "warning");
      return;
    }

    if (!idToken) {
      showToast("Session expired. Please log in again.", "error");
      return;
    }

    if (!mediaId) {
      showToast("Invalid media selection.", "error");
      return;
    }

    try {
      setLoading(true);

      await deleteMedia(mediaId, customerId, idToken);

      // Optimistically remove from the current page list.
      setItems((prev) => prev.filter((m) => m.media_id !== mediaId));

      // Close the preview modal if it's showing the deleted item
      if (preview && preview.media_id === mediaId) {
        setPreview(null);
      }

      setConfirmDeleteOpen(false);
      setPendingDeleteItem(null);
      showToast("Media deleted successfully.", "success");
      // Refresh page 1 so the count and order stay accurate.
      await fetchPage1();
    } catch (err: any) {
      debug.error("Error deleting media:", err);
      showToast(err.message || "Failed to delete media.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClosePreview = () => {
    setConfirmDeleteOpen(false);
    setPendingDeleteItem(null);
    setPreview(null);
  };

  // Modal backdrop click to close
  const modalRef = useRef<HTMLDivElement>(null);
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === modalRef.current) handleClosePreview();
  };

  return (
    <>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-theme-fg">
          View Images and Video
        </h1>
        <div className="space-y-4 rounded-lg border border-theme-border bg-theme-background p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-theme-fg">Media</h2>
              {selectedCustomerId && (
                <>
                  <button
                    type="button"
                    className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${mediaTab === "images"
                        ? "bg-theme-primary text-white hover:opacity-90"
                        : "bg-surface-ui text-theme-fg-muted hover:bg-surface-ui-hover"
                      }`}
                    onClick={() => setMediaTab("images")}
                  >
                    Images
                  </button>
                  <button
                    type="button"
                    className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${mediaTab === "videos"
                        ? "bg-theme-primary text-white hover:opacity-90"
                        : "bg-surface-ui text-theme-fg-muted hover:bg-surface-ui-hover"
                      }`}
                    onClick={() => setMediaTab("videos")}
                  >
                    Videos
                  </button>
                  <button
                    type="button"
                    className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${mediaTab === "documents"
                        ? "bg-theme-primary text-white hover:opacity-90"
                        : "bg-surface-ui text-theme-fg-muted hover:bg-surface-ui-hover"
                      }`}
                    onClick={() => setMediaTab("documents")}
                  >
                    Documents
                  </button>
                </>
              )}
            </div>
            {selectedCustomerId && showToolbarUpload && (
              <div className="flex shrink-0 flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/png,image/jpeg,image/gif,image/webp,video/mp4,video/quicktime,video/webm,application/pdf"
                  multiple
                  onChange={handleToolbarFileChange}
                />
                {/*
                  The suggestion list is attached only when the customer has
                  actually tagged something before. Offering the machine-written
                  tags ("gemini", "raw", "post:post-f64d…") made a free-text
                  field look like a dropdown with three fixed choices.
                */}
                <input
                  type="text"
                  {...(tagSuggestions.length > 0
                    ? { list: "media-tag-suggestions" }
                    : {})}
                  value={uploadTags}
                  onChange={(e) => setUploadTags(e.target.value)}
                  disabled={uploadBusy}
                  placeholder="Tag (optional), e.g. summer-event"
                  aria-label="Tag these uploads"
                  title='Tags let you ask the assistant for these later, e.g. "write posts for my summer-event images"'
                  /*
                    Golden focus, matching the inputs in the UGC flow and the
                    assistant. The previous focus:ring-theme-primary/40 read as
                    a plain blue browser ring next to the Upload button, which
                    is the one control it sits beside. ThemeManager already maps
                    #e4b372 to --color-accent-warm in dark mode, so this pair
                    themes correctly rather than being a hardcoded light-only
                    colour.
                  */
                  className="h-9 w-full rounded-md border border-theme-border bg-theme-background px-3 text-sm text-theme-fg placeholder:text-theme-fg-muted outline-none focus:border-[#e4b372] focus:outline-none focus:ring-2 focus:ring-[#e4b372]/25 disabled:opacity-50 sm:w-56"
                />
                {tagSuggestions.length > 0 && (
                  <datalist id="media-tag-suggestions">
                    {tagSuggestions.map((tag) => (
                      <option key={tag} value={tag} />
                    ))}
                  </datalist>
                )}
                <button
                  type="button"
                  disabled={uploadBusy || loading}
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-theme-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
                >
                  <span className="material-icons-outlined text-lg">upload</span>
                  {uploadBusy ? "Uploading…" : "Upload media"}
                </button>
              </div>
            )}
          </div>
          {lastUploadedVideoForSocial && onPrepareForSocial && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-theme-border bg-theme-surface px-3 py-2 text-sm">
              <span className="text-theme-fg">Latest video ready.</span>
              <button
                type="button"
                className="font-medium text-theme-primary underline hover:opacity-90"
                onClick={() => {
                  onPrepareForSocial(
                    lastUploadedVideoForSocial.videoUrl,
                    lastUploadedVideoForSocial.thumbnailUrl
                  );
                  setLastUploadedVideoForSocial(null);
                }}
              >
                Prepare for social
              </button>
              <button
                type="button"
                className="text-theme-fg-muted hover:text-theme-fg"
                onClick={() => setLastUploadedVideoForSocial(null)}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          )}
        </div>
        <div>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-theme-border border-t-theme-primary" />
              <p className="mt-4 text-sm text-theme-fg-muted">Loading media…</p>
            </div>
          ) : !selectedCustomerId ? (
            <div className="py-8 text-left text-theme-fg-muted">
              Select a business profile to view media.
            </div>
          ) : items.length === 0 ? (
            <div className="py-8 text-left text-theme-fg-muted">
              {mediaTab === "videos"
                ? "You do not have any videos in your library."
                : "You do not have any images in your library."}
            </div>
          ) : (
            <>
              <div className="columns-1 sm:columns-2 md:columns-3 gap-4 space-y-4">
                {items.map((item) => (
                  <div
                    key={item.media_id}
                    className="mb-4 break-inside-avoid flex-1"
                  >
                    <div
                      className="cursor-pointer overflow-hidden rounded-xl border border-theme-border shadow transition hover:shadow-lg"
                      onClick={() => {
                        if (isSelectionMode && onMediaSelected) {
                          handleSelectMedia(item);
                        } else {
                          setPreview(item);
                        }
                      }}
                    >
                      {item.media_type.toLowerCase() === "image" ? (
                        <img
                          src={item.cloudfront_url}
                          alt={item.s3_key}
                          className="w-full rounded-xl object-cover"
                          style={{
                            maxHeight: 400,
                            minHeight: 120,
                            objectFit: "cover",
                          }}
                          loading="lazy"
                        />
                      ) : item.media_type.toLowerCase() === "document" || item.s3_key?.toLowerCase().endsWith(".pdf") ? (
                        <div
                          className="flex flex-col items-center justify-center rounded-xl bg-surface-ui p-6 text-center shadow-inner"
                          style={{ minHeight: 160, maxHeight: 400 }}
                        >
                          <span className="material-icons-outlined text-5xl text-red-500 mb-2">picture_as_pdf</span>
                          <span className="text-xs font-semibold text-theme-fg line-clamp-2 break-all">
                            {item.s3_key.split('/').pop()}
                          </span>
                          <span className="mt-2 rounded bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700 uppercase">
                            PDF Document
                          </span>
                        </div>
                      ) : (
                        <video
                          src={item.cloudfront_url}
                          className="w-full rounded-xl object-cover"
                          style={{
                            maxHeight: 400,
                            minHeight: 120,
                            objectFit: "cover",
                          }}
                          controls={false}
                          muted
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {(currentPage > 0 || nextToken) && (
                <div className="mt-6 flex items-center justify-center gap-3">
                  <button
                    type="button"
                    disabled={currentPage === 0 || loading}
                    onClick={handlePrevPage}
                    className="inline-flex items-center gap-1 rounded-md border border-theme-border bg-surface-ui px-4 py-2 text-sm font-medium text-theme-fg transition-colors hover:bg-surface-ui-hover disabled:opacity-40"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </button>
                  <span className="text-sm text-theme-fg-muted">Page {currentPage + 1}</span>
                  <button
                    type="button"
                    disabled={!nextToken || loading}
                    onClick={handleNextPage}
                    className="inline-flex items-center gap-1 rounded-md bg-theme-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:opacity-40"
                  >
                    Next
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Modal Popup */}
      {preview && (
        <div
          ref={modalRef}
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={handleBackdropClick}
          aria-modal="true"
          role="dialog"
          style={{
            background: "rgba(0,0,0,0.7)",
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100vh",
            top: 0,
            left: 0,
            margin: 0,
            padding: 0,
            zIndex: 9999,
          }}
        >
          <div
            className="relative flex flex-col overflow-hidden rounded-2xl border border-theme-border bg-theme-surface text-theme-fg shadow-xl"
            style={{
              maxWidth: "70vw",
              maxHeight: "90vh",
              minWidth: 320,
              minHeight: 320,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            {/* Card Content */}
            <div
              className="flex flex-row items-center relative w-full"
              style={{
                flex: "1 1 auto",
                minHeight: 320,
              }}
            >
              {/* Image Section */}
              <div
                className="relative flex items-center"
                style={{
                  marginLeft: IMAGE_LEFT_MARGIN,
                  marginRight: IMAGE_RIGHT_PADDING,
                  minHeight: 320,
                }}
              >
                {preview.media_type.toLowerCase() === "image" ? (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      paddingTop: 20,
                      paddingBottom: 20,
                      boxSizing: "border-box",
                    }}
                  >
                    <img
                      src={preview.cloudfront_url}
                      alt="preview"
                      className="rounded-xl object-contain shadow"
                      style={{
                        maxHeight: "60vh",
                        maxWidth: "40vw",
                        display: "block",
                        background: "var(--color-background)",
                      }}
                    />
                  </div>
                ) : preview.media_type.toLowerCase() === "document" ||
                  preview.s3_key?.toLowerCase().endsWith(".pdf") ? (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      paddingTop: 20,
                      paddingBottom: 20,
                      boxSizing: "border-box",
                    }}
                  >
                    <iframe
                      src={preview.cloudfront_url}
                      title={preview.s3_key?.split("/").pop() || "document"}
                      className="rounded-xl shadow"
                      style={{
                        height: "60vh",
                        width: "40vw",
                        display: "block",
                        background: "var(--color-background)",
                        border: "none",
                      }}
                    />
                    <a
                      href={preview.cloudfront_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 text-sm text-theme-primary underline"
                    >
                      Open in new tab
                    </a>
                  </div>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      paddingTop: 20,
                      paddingBottom: 20,
                      boxSizing: "border-box",
                    }}
                  >
                    <video
                      src={preview.cloudfront_url}
                      controls
                      className="rounded-xl object-contain shadow"
                      style={{
                        maxHeight: "60vh",
                        maxWidth: "40vw",
                        display: "block",
                        background: "var(--color-background)",
                      }}
                    />
                  </div>
                )}
                {/* Overlay pill expansion */}
                <div
                  className="absolute top-0 right-0 flex flex-col items-end"
                  style={{
                    height: "100%",
                    justifyContent: "center",
                    gap: `${BUTTON_GAP}px`,
                    pointerEvents: "none",
                  }}
                ></div>
              </div>
              {/* Right-side Button Stack */}
              <div
                className="absolute top-0 right-0 flex flex-col items-end"
                style={{
                  top: 0,
                  right: 0,
                  alignItems: "flex-end",
                  justifyContent: "flex-start",
                  gap: `${BUTTON_GAP}px`,
                  paddingRight: BUTTON_RIGHT_MARGIN,
                  paddingTop: BUTTON_GAP,
                  height: "auto",
                }}
              >
                <CircleButton
                  icon={
                    <span className="text-xl font-bold text-theme-fg">&times;</span>
                  }
                  ariaLabel="Close"
                  onClick={handleClosePreview}
                  color="var(--color-surface-ui)"
                  border
                  disabled={loading}
                />
                <ActionPillButton
                  label={loading ? "Deleting..." : "Delete"}
                  disabled={loading || confirmDeleteOpen}
                  icon={
                    loading ? (
                      <svg
                        className="w-5 h-5 animate-spin"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="white"
                          strokeWidth="3"
                          opacity="0.25"
                        />
                        <path
                          d="M22 12a10 10 0 0 0-10-10"
                          stroke="white"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path d="M3 6h18M8 6v12a2 2 0 002 2h4a2 2 0 002-2V6M10 10v6M14 10v6" />
                      </svg>
                    )
                  }
                  onClick={() => {
                    if (!loading) handleDeleteImage(preview);
                  }}
                  ariaLabel="Delete"
                />
              </div>
            </div>
            {isSelectionMode && (
              <div className="flex w-full justify-end border-t border-theme-border bg-theme-background py-3 px-8">
                <button
                  type="button"
                  onClick={() => {
                    handleSelectMedia(preview);
                    setPreview(null);
                  }}
                  className="rounded-md bg-theme-primary px-4 py-2 text-white transition-colors hover:opacity-90"
                >
                  Select This Media
                </button>
              </div>
            )}
            {/* Info Section */}
            <div className="flex w-full flex-col items-start rounded-b-2xl border-t border-theme-border bg-theme-background px-8 py-6">
              <div className="mb-2 text-sm text-theme-fg">
                <span className="font-semibold">Created by:</span>{" "}
                {(preview.created_by || "Unknown").trim()}
              </div>
              <div className="mb-2 text-sm text-theme-fg">
                <span className="font-semibold">Uploaded:</span>{" "}
                {formatDateNoSeconds(preview.created_at)}
              </div>
              <div className="mb-2 text-sm text-theme-fg">
                <span className="font-semibold">Resolution:</span>{" "}
                {preview.metadata.dimensions}
              </div>
              <div className="mb-2 text-sm text-theme-fg">
                <span className="font-semibold">Size:</span>{" "}
                {Math.round(preview.metadata.size / 1024)} KB
              </div>
              {/*
                Always rendered, including when empty. A tag the customer typed
                at upload is otherwise invisible everywhere in the app, so there
                is no way to tell a successful tag from a silently dropped one.
              */}
              <div className="mb-2 flex flex-wrap items-center gap-2 text-sm text-theme-fg">
                <span className="font-semibold">Tags:</span>
                {userTags(preview.tags).length > 0 ? (
                  userTags(preview.tags).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-theme-primary/10 px-2.5 py-0.5 text-xs font-medium text-theme-primary"
                    >
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="text-theme-fg-muted">None</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={confirmDeleteOpen}
        title="Delete media?"
        message={`Are you sure you want to delete this ${pendingDeleteItem?.media_type?.toLowerCase() ?? "media"
          }? This action cannot be undone.`}
        confirmLabel={loading ? "Deleting..." : "Delete"}
        isConfirming={loading}
        confirmDisabled={false}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setConfirmDeleteOpen(false);
          setPendingDeleteItem(null);
        }}
      />
    </>
  );
};

// Circle Button
const CircleButton: React.FC<{
  icon: React.ReactNode;
  ariaLabel: string;
  onClick: () => void;
  color?: string;
  border?: boolean;
  disabled?: boolean;
}> = ({
  icon,
  ariaLabel,
  onClick,
  color = "var(--color-brand-primary)",
  border,
  disabled,
}) => (
    <button
      type="button"
      aria-label={ariaLabel}
      tabIndex={0}
      className={`flex items-center justify-center rounded-full shadow transition-all duration-200 focus:outline-none`}
      disabled={disabled}
      style={{
        width: BUTTON_SIZE,
        height: BUTTON_SIZE,
        background: color,
        border: border ? "1px solid var(--color-border)" : "none",
        marginBottom: BUTTON_GAP,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      onClick={onClick}
    >
      {icon}
    </button>
  );

// Action Button
const ActionPillButton: React.FC<{
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  ariaLabel: string;
  disabled?: boolean;
}> = ({ label, icon, onClick, ariaLabel, disabled }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      tabIndex={0}
      aria-label={ariaLabel}
      disabled={disabled}
      className="group flex items-center justify-end rounded-full shadow transition-all duration-200 overflow-visible relative focus:outline-none"
      style={{
        width: hovered ? 160 : BUTTON_SIZE,
        height: BUTTON_SIZE,
        background: "var(--color-brand-primary)",
        borderRadius: hovered ? 24 : "50%",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        marginBottom: BUTTON_GAP,
        position: "relative",
        zIndex: 30,
        pointerEvents: disabled ? "none" : "auto",
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => !disabled && setHovered(false)}
      onFocus={() => !disabled && setHovered(true)}
      onBlur={() => !disabled && setHovered(false)}
      onClick={onClick}
    >
      <span
        className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center transition-all duration-200 ${hovered ? "opacity-0" : "opacity-100"
          }`}
        style={{ width: 24, height: 24 }}
      >
        {icon}
      </span>
      <span
        className={`pr-4 pl-10 text-white font-semibold text-base transition-all duration-200 whitespace-nowrap ${hovered ? "opacity-100" : "opacity-0"
          }`}
        style={{
          width: hovered ? "auto" : 0,
          pointerEvents: hovered ? "auto" : "none",
        }}
      >
        {label}
      </span>
    </button>
  );
};

export default DisplayMedia;